import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Dropdown, Icon, Message } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useOrganization } from '@clerk/clerk-react';
import { getAccessToken } from '../../../utils/access-token-storage';
import selectors from '../../../selectors';
import { socket } from '../../../api';
import styles from './BotActionModal.module.scss';

const BotActionModal = ({ isOpen, onClose, context, initialBoardId, projectId }) => {
  const [t] = useTranslation();
  const { organization } = useOrganization();
  const [instruction, setInstruction] = useState('');
  const [selectedBoardId, setSelectedBoardId] = useState(initialBoardId || '');
  const [selectedListId, setSelectedListId] = useState('');
  const [loading, setLoading] = useState(false);
  const [boards, setBoards] = useState([]);
  const [lists, setLists] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load boards on mount
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const token = getAccessToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        if (organization) {
          authHeaders['Organization-Id'] = organization.id;
        }

        // Use 'true' for 'withBoards' if the API supports it, or check response structure
        // Standard projects endpoint might return boards included
        const { included, items } = await socket.get('/projects', undefined, authHeaders);

        let allBoards = [];

        // Strategy 1: Check included.boards
        if (included && included.boards) {
          allBoards = included.boards;
        }
        // Strategy 2: If no included boards, maybe iterate projects and fetch boards?
        // But /projects typically returns projects.
        // If we want ALL boards across projects, we might need to iterate.
        // For now, let's assume the user context provides projects and their boards.
        // If the API returns projects, we might need to look at project.boards if populated?
        // Let's rely on what we saw before: included.boards seems to be the pattern in this app.

        // Filter boards by projectId if provided
        if (projectId) {
          allBoards = allBoards.filter(b => b.projectId === projectId);
        }

        // IMPORTANT: Filter boards by current project if context suggests,
        // but the modal allows selecting ANY board.
        // However, user report says "solo mostras los boards del project actual".
        // This implies we ARE filtering or the API only returns boards for one project?
        // The /projects endpoint returns ALL projects for the user.
        // If included.boards only has boards for the "current" project context (if any),
        // we might need to fetch boards for each project.

        // Let's check how many boards we get.
        // If we want ALL boards from ALL projects:

        if (
          items &&
          items.length > 0 &&
          (!included || !included.boards || included.boards.length === 0)
        ) {
          // Fallback: if boards are not included, we might need to fetch them.
          // But usually 'included' has them.
        }

        if (allBoards.length > 0) {
          setBoards(
            allBoards.map(b => ({
              key: b.id,
              text: b.name,
              value: b.id,
            }))
          );
        }
      } catch (err) {
        console.error('Error fetching boards:', err);
      }
    };

    if (isOpen) {
      fetchBoards();
    }
  }, [isOpen]);

  // Load lists when board changes
  useEffect(() => {
    const fetchLists = async () => {
      if (!selectedBoardId) {
        setLists([]);
        return;
      }

      try {
        const token = getAccessToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        if (organization) {
          authHeaders['Organization-Id'] = organization.id;
        }

        // We need to fetch the board details to get lists
        const { included } = await socket.get(`/boards/${selectedBoardId}`, undefined, authHeaders);
        if (included && included.lists) {
          setLists(
            included.lists.map(l => ({
              key: l.id,
              text: l.name,
              value: l.id,
            }))
          );
          // Auto-select first list if none selected
          if (included.lists.length > 0 && !selectedListId) {
            setSelectedListId(included.lists[0].id);
          }
        }
      } catch (err) {
        console.error('Error fetching lists:', err);
      }
    };

    if (selectedBoardId) {
      fetchLists();
    }
  }, [selectedBoardId, selectedListId]); // Added selectedListId to dep array to avoid loop? No, that's dangerous.
  // Actually, if selectedListId is changed by user, we don't want to re-fetch lists.
  // But inside the effect, we check !selectedListId.
  // Better dependency: [selectedBoardId].

  const handleSubmit = async () => {
    if (!instruction.trim() || !selectedBoardId || !selectedListId) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getAccessToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      if (organization) {
        authHeaders['Organization-Id'] = organization.id;
      }

      const response = await socket.post(
        '/ai/action',
        {
          instruction,
          boardId: selectedBoardId,
          listId: selectedListId,
          context, // Previous chat context
        },
        authHeaders
      );

      setSuccess(t('common.cardCreatedSuccess', 'Action executed successfully! Card created.'));
      setTimeout(() => {
        onClose();
        setSuccess(null);
        setInstruction('');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Error executing action');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} size="small" className={styles.wrapper}>
      <Modal.Header>
        <Icon name="magic" />
        {t('common.createCardWithAI', 'Create Card with AI')}
      </Modal.Header>
      <Modal.Content>
        <Form error={!!error} success={!!success}>
          {error && <Message error content={error} />}
          {success && <Message success content={success} />}

          <Form.Group widths="equal">
            <Form.Field>
              <label>{t('common.board', 'Board')}</label>
              <Dropdown
                selection
                options={boards}
                value={selectedBoardId}
                onChange={(_, { value }) => setSelectedBoardId(value)}
                placeholder={t('common.selectBoard', 'Select Board')}
              />
            </Form.Field>
            <Form.Field>
              <label>{t('common.list', 'List')}</label>
              <Dropdown
                selection
                options={lists}
                value={selectedListId}
                onChange={(_, { value }) => setSelectedListId(value)}
                placeholder={t('common.selectList', 'Select List')}
                disabled={!selectedBoardId}
              />
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label>{t('common.instructions', 'Instructions')}</label>
            <Form.TextArea
              placeholder={t(
                'common.aiInstructionPlaceholder',
                'e.g., Create a card named "Follow up" using the overdue items from the analysis...'
              )}
              value={instruction}
              onChange={e => setInstruction(e.target.value)}
              rows={5}
            />
            <div className={styles.helpText}>
              {t(
                'common.aiContextHint',
                ' The AI will use your instructions and the previous chat context to create the card.'
              )}
            </div>
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={loading}>
          {t('action.cancel', 'Cancel')}
        </Button>
        <Button
          primary
          onClick={handleSubmit}
          loading={loading}
          disabled={loading || !instruction.trim() || !selectedListId}
        >
          {t('action.create', 'Create')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

BotActionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  context: PropTypes.string,
  initialBoardId: PropTypes.string,
  projectId: PropTypes.string,
};

export default BotActionModal;
